import { handleSave } from "../hooks/handleSave";
import { handleAnalyze } from "../hooks/handleAnalyze";
import { RowData } from "../types";
import { useToast } from "../../../shared/hooks";

interface UseAnalysisActionsParams {
	data: RowData[];
	setData: React.Dispatch<React.SetStateAction<RowData[]>>;
	setPayloads: React.Dispatch<React.SetStateAction<any[]>>;
	setLoading: (value: boolean) => void;
	logs: boolean;
}

export const useAnalysisActions = ({ data, setData, setPayloads, setLoading, logs }: UseAnalysisActionsParams) => {
	const toast = useToast();

	const handleAnalyzeClick = async () => {
		setLoading(true);

		try {
			const result = await handleAnalyze(data, logs);

			// payloads já vêm com: { payload, originalIndex }
			const indexedPayloads = result.payloads;

			// atualiza as linhas com erro, mantendo as demais intactas
			const updated = data.map((row) => {
				const erro = result.erros.find((r) => r.RowNumber === row.RowNumber);
				return erro ? { ...row, ...erro } : row;
			});

			setData(updated);
			setPayloads(indexedPayloads);

			toast.present({
				messageOrContent: `Payloads gerados: ${indexedPayloads.length}`,
				type: indexedPayloads.length ? "success" : "error",
				position: "top-right",
			});
		} catch (error) {
			console.error("Erro ao analisar:", error);
			toast.present({
				messageOrContent: "Erro durante a análise.",
				type: "error",
				position: "top-right",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleSaveClick = async (payloads: any[]) => {
		if (!payloads.length) {
			return toast.present({
				messageOrContent: "Nenhum payload para salvar.",
				type: "error",
			});
		}

		const pending = payloads.filter((p) => {
			const row = data.find((r) => r.RowNumber === p.originalIndex);
			return row?.status !== "salvo";
		});

		if (!pending.length) {
			return toast.present({
				messageOrContent: "Nenhum registro novo para salvar.",
				type: "error",
			});
		}

		setLoading(true);

		try {
			const result = await handleSave(
				pending.map((p) => p.payload),
				data,
				"novo",
				logs,
				pending.map((p) => p.originalIndex)
			);

			console.log("📦 Resultados do handleSave:", result);

			const updated = data.map((row) => {
				const found = result.find((r) => r.success && r.rowIndex === row.RowNumber);
				return found
					? {
							...row,
							status: "salvo",
							mensagem: found.message,
					  }
					: row;
			});

			setData(updated);

			const total = result.filter((r) => r.success).length;
			toast.present({
				messageOrContent: `${total} registros salvos com sucesso.`,
				type: total ? "success" : "error",
			});
		} catch (error) {
			console.error("Erro ao salvar:", error);
			toast.present({
				messageOrContent: "Erro ao salvar os registros.",
				type: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	return {
		handleAnalyzeClick,
		handleSaveClick,
	};
};
